import { Stack, Text } from 'braid-design-system';
import { SmartTextLink } from 'scoobie';

import { CredentialsProvider } from './CredentialsContext.tsx';
import { CredentialList } from './components/CredentialList/CredentialList.tsx';

import { BasePage } from '#src/components/BasePage/BasePage.tsx';
import type { Breadcrumb } from '#src/components/Breadcrumbs/Breadcrumbs.tsx';
import { NoPermissionAlert } from '#src/components/NoPermissionAlert/NoPermissionAlert.tsx';
import { usePermissions } from '#src/hooks/auth.tsx';

export const CredentialsPage = ({
  breadcrumbs,
}: {
  breadcrumbs: Breadcrumb[];
}) => {
  const { permissions } = usePermissions();

  const hasReadAccess = permissions.includes('query:credentials');

  return (
    <BasePage title="Client credentials" breadcrumbs={breadcrumbs}>
      <Stack space="medium">
        <Text>
          Manage your live and Playground client credentials for accessing the
          SEEK API.
        </Text>

        <Text>
          Provision and use Playground credentials for read-only access to the
          SEEK API’s mock data. This can be useful for testing your
          authentication flows, GraphQL basics, SEEK API connectivity, and data
          structures / mapping. Mutation requests will be validated against the
          data model, but will reject any requests to change data.
        </Text>

        <Text>
          Once you’ve reached the limits of what you can do with the Playground,
          provision your live credentials and start building against the
          production SEEK API. You can create up to 2 live credentials to
          facilitate zero-downtime credential rotation.
        </Text>

        <Text>
          Review the documentation for more about our{' '}
          <SmartTextLink href="/auth#authentication">
            authentication flow
          </SmartTextLink>{' '}
          and{' '}
          <SmartTextLink href="/graphql/playground">
            Playground environment
          </SmartTextLink>
          .
        </Text>
      </Stack>

      {hasReadAccess ? (
        <CredentialsProvider>
          <CredentialList />
        </CredentialsProvider>
      ) : (
        <NoPermissionAlert subject="view credentials" />
      )}
    </BasePage>
  );
};
